import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { proposalAPI } from "../../api/gms/proposal/proposal-api";
import { socketSetup } from "../../library/socket-io/socket-setup";
import { useModalAccess } from "../../components/global/modal/use-modal-access";

interface SocketPayload {
    proposalId: number;
}

export const useProposalsSocket = () => {
    const dispatch = useDispatch();
    const { currentProposalId } = useModalAccess();

    const invalidate = useCallback(
        (payload?: SocketPayload | null) => {
            // Always refresh the Proposal list.
            dispatch(
                proposalAPI.util.invalidateTags([
                    { type: "Proposal", id: "LIST" },
                ])
            );

            // Always refresh the Existing Group request list too.
            dispatch(
                proposalAPI.util.invalidateTags([
                    { type: "RequestExistingGroup", id: "LIST" },
                ])
            );

            // Safely support either payload ID property.
            const proposalId = payload?.proposalId ?? payload?.proposalId;

            // Refresh proposal details only when this proposal is open.
            if (
                proposalId == null ||
                Number(proposalId) !== Number(currentProposalId)
            ) {
                return;
            }

            dispatch(
                proposalAPI.util.invalidateTags([
                    { type: "Proposal", id: proposalId },
                ])
            );
        },
        [dispatch, currentProposalId]
    );

    useEffect(() => {
        if (!socketSetup) return;

        const events = [
            "transferApplicationOwnership",
            "updateEffectiveDate",
            "updateApplication",
            
            "createApplication",
            "uploadExcelFile",
            "uploadSupportingDetails",
            "updateApplication",
            "updateApplicationStatus",
            "saveDraft",
            "setStatusChecking",
            "setStatusApproved",
            "setStatusBooked",
            "saveRates",
            "saveEvidenceNotes",
            "saveTotalAnnualPremium",
            "releaseApplication",
            "saveNotes",
            "saveActuarialToCfeNotes",
            "uploadActuarialFiles",

            "approveExtension",
            "rejectExtension",
            "requestExtension",

            "actuarialPassApproval",
            "actuarialDenyApproval",
            "actuarialSubmitApproval",

            "requestAmendment",
            "approveAmendment",
            "declineAmendment",

            "approveConflictProposal",
            "conflictProposalApproved",
            "rejectConflictProposal",
            "conflictProposalDeclined",
        ];

        events.forEach((event) => {
            socketSetup.on(event, invalidate);
        });

        return () => {
            events.forEach((event) => {
                socketSetup.off(event, invalidate);
            });
        };
    }, [invalidate]);
};